/**
 * Video Lightbox
 *
 * Controls the muted looping teaser behind the play button.
 * ModalOpener and ModalDialog in global.js control the modal itself.
 */
class VideoLightbox extends HTMLElement {
  connectedCallback() {
    this.media = this.querySelector(".video-lightbox__loop");
    this.toggle = this.querySelector(".video-lightbox__loop-toggle");

    if (!this.toggle) return;

    // A cover image can stand in for the video. There is then nothing to pause.
    if (!this.media || this.media.tagName !== "VIDEO") {
      this.toggle.hidden = true;
      return;
    }

    this.pauseIcon = this.toggle.querySelector("[data-pause]");
    this.playIcon = this.toggle.querySelector("[data-play]");
    this.userPaused = false;

    // Follow the video's own events. The button then stays correct even when
    // something else pauses the video, such as pauseAllMedia() on modal open.
    this.media.addEventListener("play", this.syncToggle.bind(this));
    this.media.addEventListener("pause", this.syncToggle.bind(this));
    this.toggle.addEventListener("click", this.toggleLoop.bind(this));

    // ModalDialog.hide() dispatches modalClosed BEFORE it calls pauseAllMedia().
    // Resuming synchronously would therefore be undone. Wait one tick.
    this.onModalClosed = () => {
      if (this.userPaused || this.prefersReducedMotion) return;
      setTimeout(() => this.play(), 0);
    };
    document.body.addEventListener("modalClosed", this.onModalClosed);
    this.watchModal();

    if (this.prefersReducedMotion) {
      this.userPaused = true;
      this.media.pause();
    }

    this.syncToggle();
  }

  /**
   * ModalDialog.show() calls pauseAllMedia() AFTER DeferredMedia has started the
   * lightbox video, which stops it the moment it starts. Start it again on the
   * next tick, once show() has finished.
   */
  watchModal() {
    const opener = this.closest("modal-opener");
    const selector = opener && opener.getAttribute("data-modal");
    this.modal = selector ? document.querySelector(selector) : null;

    if (!this.modal) return;

    this.modalObserver = new MutationObserver(() => {
      if (!this.modal.hasAttribute("open")) return;

      setTimeout(() => {
        const video = this.modal.querySelector(".modal-video__video video");
        if (!video || !video.paused) return;
        // A browser may still refuse to start a video that carries sound.
        const started = video.play();
        if (started) started.catch(() => {});
      }, 0);
    });

    this.modalObserver.observe(this.modal, {
      attributes: true,
      attributeFilter: ["open"],
    });
  }

  disconnectedCallback() {
    if (this.onModalClosed) {
      document.body.removeEventListener("modalClosed", this.onModalClosed);
    }
    if (this.modalObserver) this.modalObserver.disconnect();
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  play() {
    // Autoplay can still be refused. A rejected promise is not an error here.
    const started = this.media.play();
    if (started) started.catch(() => {});
  }

  toggleLoop() {
    if (this.media.paused) {
      this.userPaused = false;
      this.play();
    } else {
      this.userPaused = true;
      this.media.pause();
    }
  }

  syncToggle() {
    const paused = this.media.paused;

    this.toggle.setAttribute(
      "aria-label",
      paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause
    );

    if (this.pauseIcon) this.pauseIcon.hidden = paused;
    if (this.playIcon) this.playIcon.hidden = !paused;
  }
}

customElements.define("video-lightbox", VideoLightbox);
